
import React, { useState, useEffect } from "react";
import { Card, Container, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DeleteModal from "./deletepopup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaEdit, FaTrash } from "react-icons/fa";
import { getActivity, deleteActivity } from "../../store/slices/activity";

export default function Activity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { profile } = useSelector((state) => state.auth);

  const {
    activityList = [],
    loading,
    totalDataCount = 0,
  } = useSelector((state) => state.activity);

  // =====================================
  // PERMISSIONS
  // =====================================

  const isStaff =
    profile?.userType === "STAFF" ||
    profile?.role === "STAFF";

  const activityPermission = profile?.permissions?.activity;

  const permissions = Array.isArray(activityPermission)
    ? activityPermission
    : [activityPermission];

  const hasLevel = (levels) => {
    if (!isStaff) return true;

    return permissions.some((permission) =>
      levels.includes(String(permission || "").toUpperCase())
    );
  };

  const canView = hasLevel(["VIEW", "EDIT", "FULL"]);
  const canAdd = hasLevel(["EDIT", "FULL"]);
  const canEdit = hasLevel(["EDIT", "FULL"]);
  const canDelete = hasLevel(["FULL"]);

  // =====================================
  // STATES
  // =====================================

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showDelete, setShowDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState("confirm");

  const [currentPage, setCurrentPage] = useState(1);

  const pagelimit = 10;

  // =====================================
  // SEARCH DEBOUNCE
  // =====================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // =====================================
  // FETCH ACTIVITY
  // =====================================

  useEffect(() => {
    if (!canView) return;

    const params = {
      page: currentPage,
      limit: pagelimit,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    dispatch(getActivity(params));
  }, [dispatch, currentPage, debouncedSearch, canView]);

  const paginatedData = activityList;
  const totalPages = Math.ceil(totalDataCount / pagelimit);

  // =====================================
  // DELETE ACTIVITY
  // =====================================

  const handleConfirmDelete = async () => {
    if (!canDelete) return;

    try {
      setDeleteStatus("loading");

      await dispatch(deleteActivity(selectedId)).unwrap();

      setDeleteStatus("success");

      setTimeout(() => {
        setShowDelete(false);
        setDeleteStatus("confirm");
      }, 1500);

    } catch (error) {
      console.log(error);
      setDeleteStatus("confirm");
    }
  };

  // =====================================
  // ACCESS DENIED
  // =====================================

  if (!canView) {
    return (
      <Container>
        <div className="text-center py-5">
          <h5>Access Denied</h5>
          <p>You don't have permission to view Activities.</p>
        </div>
      </Container>
    );
  }

  // =====================================
  // RENDER
  // =====================================

  return (
    <>
      {/* BREADCRUMB */}
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span
            className="breadcrumb-link inactive"
            onClick={() => navigate("/admin/activity")}
          >
            Activity
          </span>
        </div>

        {/* ADD ACTIVITY: EDIT / FULL */}
        {canAdd && (
          <button
            className="add-outlet-btn"
            onClick={() => navigate("/admin/addactivity")}
          >
            + Add Activity
          </button>
        )}
      </div>

      {/* TABLE */}
      <Container>
        <Card className="table-border mb-5">
          <Card.Body>
            <div className="common-table-wrapper">

              {/* SEARCH */}
              <div className="d-flex gap-2 mb-3 ms-2 mt-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: "250px" }}
                />

                <button
                  className="cancel-btn"
                  onClick={() => setSearch("")}
                >
                  Reset
                </button>
              </div>

              <table className="common-table">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Image</th>
                    <th className="text-center">Activity Name</th>
                    <th className="text-center">Description</th>

                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          <div className="skeleton skeleton-text small"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-image"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-text"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-text"></div>
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <div className="skeleton skeleton-btn"></div>
                              <div className="skeleton skeleton-btn"></div>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id}>
                        <td className="text-center">
                          {(currentPage - 1) * pagelimit + index + 1}
                        </td>

                        <td className="text-center">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                          ) : (
                            <span className="no-image">—</span>
                          )}
                        </td>

                        <td className="text-center">
                          {item.name}
                        </td>

                        <td
                          style={{
                            maxWidth: "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${item._id}`}>
                                {item.description}
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: "pointer" }}>
                              {item.description}
                            </span>
                          </OverlayTrigger>
                        </td>

                        {/* ACTIONS */}
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="action-btn-group">

                              {/* EDIT */}
                              {canEdit && (
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() =>
                                    navigate(`/admin/editactivity/${item._id}`)
                                  }
                                >
                                  <FaEdit size={14} />
                                </button>
                              )}

                              {/* DELETE */}
                              {canDelete && (
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => {
                                    setSelectedId(item._id);
                                    setShowDelete(true);
                                  }}
                                >
                                  <FaTrash size={14} />
                                </button>
                              )}

                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={canEdit || canDelete ? 5 : 4}
                        className="text-center py-4"
                      >
                        No activities found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {!loading && totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap">

                <Button
                  variant="light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ‹
                </Button>

                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                )
                  .filter((page) =>
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 1
                  )
                  .map((page, index, arr) => {
                    const prevPage = arr[index - 1];

                    return (
                      <div
                        key={page}
                        className="d-flex align-items-center gap-2"
                      >
                        {prevPage && page - prevPage > 1 && (
                          <span>...</span>
                        )}

                        <Button
                          variant={
                            currentPage === page ? "dark" : "light"
                          }
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      </div>
                    );
                  })}

                <Button
                  variant="light"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  ›
                </Button>
              </div>
            )}

          </Card.Body>
        </Card>
      </Container>

      {/* DELETE MODAL */}
      {canDelete && (
        <DeleteModal
          show={showDelete}
          onHide={() => setShowDelete(false)}
          onConfirm={handleConfirmDelete}
          status={deleteStatus}
        />
      )}
    </>
  );
}
